import { MOBILE_NAV_GROUP_ORDER, type MobileNavGroupId } from '@/config/mobileNav';

const MOBILE_NAV_PREFS_KEY = 'mythkit_mobile_nav_v1';
const MOBILE_NAV_SLOTS = 3;

export interface MobileNavPrefs {
  groupOrder: MobileNavGroupId[];
}

function uniqueValidGroupIds(candidate: unknown): MobileNavGroupId[] {
  if (!Array.isArray(candidate)) {
    return [];
  }

  const valid = new Set<MobileNavGroupId>(MOBILE_NAV_GROUP_ORDER);
  const next: MobileNavGroupId[] = [];

  for (const value of candidate) {
    if (typeof value !== 'string') continue;
    if (!valid.has(value as MobileNavGroupId)) continue;
    if (next.includes(value as MobileNavGroupId)) continue;
    next.push(value as MobileNavGroupId);
  }

  return next;
}

export function getDefaultMobileNavPrefs(): MobileNavPrefs {
  return {
    groupOrder: [...MOBILE_NAV_GROUP_ORDER],
  };
}

export function normalizeMobileNavPrefs(input: unknown): MobileNavPrefs {
  const defaults = getDefaultMobileNavPrefs();
  if (!input || typeof input !== 'object') {
    return defaults;
  }

  const raw = input as Partial<MobileNavPrefs>;
  const order = uniqueValidGroupIds(raw.groupOrder).slice(0, MOBILE_NAV_SLOTS);

  for (const fallback of defaults.groupOrder) {
    if (order.length >= MOBILE_NAV_SLOTS) break;
    if (order.includes(fallback)) continue;
    order.push(fallback);
  }

  return { groupOrder: order };
}

export function readMobileNavPrefs(): MobileNavPrefs {
  if (typeof window === 'undefined') {
    return getDefaultMobileNavPrefs();
  }

  try {
    const raw = window.localStorage.getItem(MOBILE_NAV_PREFS_KEY);
    if (!raw) return getDefaultMobileNavPrefs();
    return normalizeMobileNavPrefs(JSON.parse(raw));
  } catch {
    return getDefaultMobileNavPrefs();
  }
}

export function writeMobileNavPrefs(nextPrefs: MobileNavPrefs): MobileNavPrefs {
  const normalized = normalizeMobileNavPrefs(nextPrefs);
  if (typeof window === 'undefined') {
    return normalized;
  }

  try {
    window.localStorage.setItem(MOBILE_NAV_PREFS_KEY, JSON.stringify(normalized));
    window.dispatchEvent(new CustomEvent('mythkit:mobile-nav-updated'));
  } catch {
    // Ignore write failures and continue with normalized in-memory value.
  }

  return normalized;
}

export function resetMobileNavPrefs(): MobileNavPrefs {
  const defaults = getDefaultMobileNavPrefs();
  return writeMobileNavPrefs(defaults);
}
